"use client";

import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";

import { GroqImage } from "@/helpers/Image";
import { PageCareerOverviewSchema } from "@/sanity/pages/PageCareerOverviewTemplate";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

const ease = [0.25, 0.1, 0.25, 1] as const;

function glueRankToNextWord(blocks: NonNullable<PageCareerOverviewSchema["heroTitle"]>) {
  return blocks.map((block) =>
    Array.isArray(block.children)
      ? {
          ...block,
          children: block.children.map((child) =>
            child?._type === "span" && typeof child.text === "string"
              ? { ...child, text: child.text.replace(/(#\d+) /g, "$1\u00A0") }
              : child,
          ),
        }
      : block,
  );
}

const MOBILE_BASE_BLUR_LAYERS = [
  { blur: "0.3rem", mask: "linear-gradient(to bottom, transparent 0%, black 25%)" },
  { blur: "0.52rem", mask: "linear-gradient(to bottom, transparent 25%, black 50%)" },
  { blur: "0.67rem", mask: "linear-gradient(to bottom, transparent 50%, black 75%)" },
  { blur: "0.79rem", mask: "linear-gradient(to bottom, transparent 75%, black 100%)" },
];

export default function CareerOverviewHero({ data }: { data: PageCareerOverviewSchema }) {
  if (!data.heroTitle && !data.heroImage) return null;

  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden bg-white pt-[5.5rem] lg:min-h-[104rem] lg:justify-start lg:pt-[8rem]">
      {data.heroBackgroundImage && (
        <div
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden="true">
          <GroqImage
            src={data.heroBackgroundImage}
            alt=""
            quality={100}
            className="!h-full !w-full !object-cover"
          />
        </div>
      )}

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 hidden h-[55rem] lg:block"
        style={{
          backgroundImage: "url(/careers/hero-dots.png)",
          backgroundSize: "137.5rem auto",
          backgroundRepeat: "repeat",
          opacity: 0.38,
          maskImage: "radial-gradient(120% 100% at 50% 100%, black 25%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(120% 100% at 50% 100%, black 25%, transparent 80%)",
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto flex w-[min(122rem,calc(100%-4rem))] flex-col items-center gap-[1.25rem]">
        {data.heroChip && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="mb-0 text-center font-blacker text-[2rem] leading-[1.5] text-black italic">
            {data.heroChip}
          </motion.p>
        )}

        {data.heroTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.25 }}
            className="richtext w-full [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance] prose-headings:mb-0 prose-headings:text-[5rem] prose-headings:leading-[1.12] max-lg:prose-headings:tracking-[-0.15rem] lg:prose-headings:tracking-[-0.21rem] lg:prose-headings:text-[7rem]">
            <PortableText
              value={glueRankToNextWord(data.heroTitle)}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data.heroSubtitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.35 }}
            className="richtext w-full max-sm:max-w-[46.5rem] sm:max-w-[min(86.5rem,88%)] prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:font-medium prose-p:text-black/70 prose-p:[text-wrap:balance]">
            <PortableText
              value={data.heroSubtitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data.heroCTA?.cta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.45 }}
            className="mt-[1.25rem] w-full lg:w-fit lg:min-w-[29.75rem] [&_.button]:text-[1.75rem] [&_.button]:whitespace-nowrap lg:[&_.button]:w-full">
            <CTAButton data={data.heroCTA} />
          </motion.div>
        )}
      </div>

      {data.heroImage && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease, delay: 0.3 }}
          className="pointer-events-none relative z-0 mt-[5rem] h-[56rem] w-full overflow-hidden lg:hidden"
          aria-hidden="true">
          <GroqImage
            src={data.heroImage}
            alt=""

            quality={100}
            style={{ width: "236.5%", height: "auto", left: "-70%", top: "-12.9%" }}
            className="!absolute !max-w-none"
          />

          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[17.375rem]">
            {MOBILE_BASE_BLUR_LAYERS.map(({ blur, mask }) => (
              <div
                key={blur}
                className="absolute inset-0"
                style={{
                  backdropFilter: `blur(${blur})`,
                  WebkitBackdropFilter: `blur(${blur})`,
                  maskImage: mask,
                  WebkitMaskImage: mask,
                }}
              />
            ))}
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.6) 100%)" }}
            />
          </div>
        </motion.div>
      )}

      {data.heroImage && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease, delay: 0.3 }}
          className="pointer-events-none relative -left-[3.5625rem] z-0 -mt-[9rem] hidden h-[74.75rem] w-[196.625rem] shrink-0 overflow-hidden lg:block"
          aria-hidden="true">
          <GroqImage
            src={data.heroImage}
            alt=""

            quality={100}
            style={{ width: "113.6%", height: "168.09%", left: "-7.12%", top: "-0.02%" }}
            className="!absolute !max-w-none"
          />
        </motion.div>
      )}

      {data.heroImage && (
        <div
          className="pointer-events-none absolute inset-x-0 z-[1] hidden backdrop-blur-[2px] lg:block"
          style={{
            bottom: "-32rem",
            height: "39.875rem",
            background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgb(255,255,255) 59.091%)",
            maskImage: "linear-gradient(to bottom, transparent 0%, black 15%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 15%)",
          }}
          aria-hidden="true"
        />
      )}
    </section>
  );
}
